import React, { useState, useEffect } from 'react';
import { statsAPI, followUpsAPI } from '../services/api';
import { Card } from '../components/ui/card';
import {
  Users,
  UserCheck,
  TrendingUp,
  Crown,
  CalendarClock,
  Ticket,
  Clock,
  ChevronRight,
  Check,
  Trash2,
  Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, followUpsRes] = await Promise.all([
          statsAPI.get(),
          followUpsAPI.getAll(false)
        ]);
        setStats(statsRes.data);
        setFollowUps(followUpsRes.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCompleteFollowUp = async (id) => {
    try {
      await followUpsAPI.update(id, { completed: true, status: 'Completed' });
      setFollowUps(followUps.filter(f => f.id !== id));
    } catch (error) {
    }
  };

  const handleDeleteFollowUp = async (id) => {
    try {
      await followUpsAPI.delete(id);
      setFollowUps(followUps.filter(f => f.id !== id));
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Customer Statistics</h1>
        <p className="text-gray-500">All Time</p>
      </div>

      {/* Top Stats Row */}
      <div className="grid grid-cols-3 gap-6">
        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-start gap-3">
            <Users className="text-blue-500" size={20} />
            <div>
              <p className="text-blue-500 text-sm font-medium">Total Unique Customers</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{stats?.total_unique_customers || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-start gap-3">
            <UserCheck className="text-amber-500" size={20} />
            <div>
              <p className="text-amber-500 text-sm font-medium">One-Time Buyers</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">{stats?.one_time_buyers || 0}</p>
              <p className="text-gray-500 text-sm">{stats?.one_time_buyers_percent || 0}% of total</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-start gap-3">
            <TrendingUp className="text-green-500" size={20} />
            <div>
              <p className="text-green-500 text-sm font-medium">Repeat Customers</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats?.repeat_customers || 0}</p>
              <p className="text-gray-500 text-sm">{stats?.repeat_customers_percent || 0}% of total</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Orders and Revenue Row */}
      <div className="grid grid-cols-2 gap-6">
        <Card className="p-8 bg-white border border-gray-100 text-center">
          <p className="text-gray-500 text-sm font-medium">Total Orders</p>
          <p className="text-5xl font-bold text-gray-900 mt-2">{stats?.total_orders || 0}</p>
        </Card>

        <Card className="p-8 bg-white border border-gray-100 text-center">
          <p className="text-gray-500 text-sm font-medium">Total Revenue</p>
          <p className="text-5xl font-bold text-gray-900 mt-2">
            {stats?.currency || 'RM'}{(stats?.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-4 gap-6">
        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Clients</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.active_clients || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
              <Users className="text-amber-500" size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">VIP Clients</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.vip_clients || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center">
              <Crown className="text-yellow-500" size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Pending Follow-ups</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.pending_follow_ups || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
              <CalendarClock className="text-orange-500" size={20} />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Coupons</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.active_coupons || 0}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
              <Ticket className="text-blue-500" size={20} />
            </div>
          </div>
        </Card>
      </div>

      {/* Upcoming Follow-ups */}
      <Card className="p-6 bg-white border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Upcoming Follow-ups</h2>
          <Link
            to="/follow-ups"
            className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm"
          >
            View All
            <ChevronRight size={16} />
          </Link>
        </div>

        <div className="space-y-4">
          {followUps.slice(0, 4).map((followUp) => (
            <div
              key={followUp.id}
              className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0"
            >
              <div className="flex items-center gap-4">
                <Clock className="text-gray-400" size={18} />
                <div>
                  <p className="font-medium text-gray-900">{followUp.client_name}</p>
                  <p className="text-sm text-gray-500">{followUp.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                  {followUp.status}
                </span>
                <button 
                  onClick={() => handleCompleteFollowUp(followUp.id)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Check className="text-gray-400" size={18} />
                </button>
                <button 
                  onClick={() => handleDeleteFollowUp(followUp.id)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Trash2 className="text-gray-400" size={18} />
                </button>
              </div>
            </div>
          ))}
          {followUps.length === 0 && (
            <p className="text-gray-500 text-center py-4">No pending follow-ups</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DashboardPage;
